export type FallLineSide = "left" | "right";
export type BallHeightVsHole = "above" | "below";
export type BreakDirection = "left-to-right" | "right-to-left";

export type FallLineBreakResult = {
  breakDirection: BreakDirection;
  angleWeight: number;
  fallLineInfluence: number;
  explanation: string;
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Quadrant-based fall-line break direction.
 *
* A. Above hole + right of fall line => right-to-left
* B. Above hole + left of fall line  => left-to-right
* C. Below hole + right of fall line => left-to-right
* D. Below hole + left of fall line  => right-to-left
 */
export function inferFallLineBreakDirection(args: {
  sideOfFallLine: FallLineSide;
  heightVsHole: BallHeightVsHole;
}): BreakDirection {
  const { sideOfFallLine, heightVsHole } = args;

  if (heightVsHole === "above" && sideOfFallLine === "right") {
    return "left-to-right";
  }

  if (heightVsHole === "above" && sideOfFallLine === "left") {
    return "right-to-left";
  }

  if (heightVsHole === "below" && sideOfFallLine === "right") {
    return "right-to-left";
  }

  return "left-to-right";
}

/**
 * Angle weighting:
 * - 0° or 180° = putt mostly along the fall line, less lateral fall-line effect
 * - 90° = maximum cross-fall effect
 */
export function fallLineAngleWeight(angleToFallLineDeg: number): number {
  const a = clamp(Math.abs(angleToFallLineDeg), 0, 180);
  return Math.sin((a * Math.PI) / 180);
}

export function computeFallLineBreak(args: {
  sideOfFallLine: FallLineSide;
  heightVsHole: BallHeightVsHole;
  angleToFallLineDeg: number;
  localSlopeDeg: number;
  mtbStrengthDeg?: number;
  fallLineWeight?: number;
}): FallLineBreakResult {
  const {
    sideOfFallLine,
    heightVsHole,
    angleToFallLineDeg,
    localSlopeDeg,
    mtbStrengthDeg = 0,
    fallLineWeight = 1,
  } = args;

  const breakDirection = inferFallLineBreakDirection({
    sideOfFallLine,
    heightVsHole,
  });

  const angleWeight = fallLineAngleWeight(angleToFallLineDeg);

  const safeLocalSlope = Number.isFinite(localSlopeDeg) ? localSlopeDeg : 0;
  const safeMtb = Number.isFinite(mtbStrengthDeg) ? mtbStrengthDeg : 0;

  const fallLineInfluence =
    angleWeight * fallLineWeight * (safeLocalSlope + 0.35 * safeMtb);

  return {
    breakDirection,
    angleWeight,
    fallLineInfluence,
    explanation:
      `${heightVsHole} hole, ${sideOfFallLine} of fall line → ` +
      `${breakDirection}; angle weight ${angleWeight.toFixed(2)}, ` +
      `fall-line influence ${fallLineInfluence.toFixed(2)}.`,
  };
}

export type FallLineBreakDirection =
  | "left-to-right"
  | "right-to-left"
  | "straight";

export type FallLineCoordinateRead = {
  breakDirection: FallLineBreakDirection;
  puttGrade: "downhill" | "uphill" | "level";
  angleWeight: number;
  distanceFromFallLineFt: number;
  distanceAlongFallLineFt: number;
  fallLineInfluence: number;
  explanation: string;
};

function safeFinite(value: number, fallback = 0): number {
  return Number.isFinite(value) ? value : fallback;
}

/**
 * Coordinate-based fall-line model.
 *
 * Player stands at ball looking toward hole.
 * Hole/fall-line intersection is (0,0).
 *
 * Y axis = fall line.
 * +Y = downhill putt.
 * -Y = uphill putt.
 *
 * X axis = perpendicular offset from fall line.
 * +X = ball right of fall line.
 * -X = ball left of fall line.
 *
 * Break direction:
 * x < 0 => left-to-right
 * x > 0 => right-to-left
 * x = 0 => straight / fall-line putt
 */
export function computeFallLineCoordinateRead(args: {
  xFromFallLineFt: number;
  yFromHoleFt: number;
  localSlopeDeg: number;
  mtbStrengthDeg?: number;
  fallLineWeight?: number;
}): FallLineCoordinateRead {
  const x = safeFinite(args.xFromFallLineFt);
  const y = safeFinite(args.yFromHoleFt);
  const localSlopeDeg = safeFinite(args.localSlopeDeg);
  const mtbStrengthDeg = safeFinite(args.mtbStrengthDeg ?? 0);
  const fallLineWeight = safeFinite(args.fallLineWeight ?? 1, 1);

  const distance = Math.sqrt(x * x + y * y);

  const angleWeight = distance > 0 ? Math.abs(x) / distance : 0;

  const breakDirection: FallLineBreakDirection =
    Math.abs(x) < 0.01
      ? "straight"
      : x < 0
        ? "left-to-right"
        : "right-to-left";

  const puttGrade =
    Math.abs(y) < 0.01 ? "level" : y > 0 ? "downhill" : "uphill";

  const fallLineInfluence =
    angleWeight * fallLineWeight * (localSlopeDeg + 0.35 * mtbStrengthDeg);

  return {
    breakDirection,
    puttGrade,
    angleWeight,
    distanceFromFallLineFt: x,
    distanceAlongFallLineFt: y,
    fallLineInfluence,
    explanation:
      `Ball at (${x.toFixed(1)}, ${y.toFixed(1)}) relative to fall line: ` +
      `${puttGrade}, ${x < 0 ? "left" : x > 0 ? "right" : "on"} of fall line → ` +
      `${breakDirection}; angle weight ${angleWeight.toFixed(2)}, ` +
      `fall-line influence ${fallLineInfluence.toFixed(2)}.`,
  };
}